import { BasePageComponent } from "@/components/base/BasePageComponent";
import { ConfirmDialogComponent } from "@/components/confirm-dialog/confirm-dialog.component";
import { PropertyHelper } from "@/helpers/property.helper";
import { UserHelper } from "@/helpers/user.helper";
import { AlertService } from "@/services/alert.service";
import { AppConfigService } from "@/services/app-config.service";
import { MenuToNative, MenuToNativeId } from "@/services/bridge/bridge.service";
import { NavService } from "@/services/nav.service";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import {
  DocumentCategory,
  PropertiesService,
  RealProperty,
  SharingService,
} from "@eemo/api-client";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { PropertySummaryComponent } from "../property-summary/property-summary.component";

import { TooltipDirective } from "@/directives/tooltip/tooltip.directive";
import { ActionService } from "@/services/action.service";
import { NgFor, NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { RoundedLinkComponent } from "../../../components/rounded-link/rounded-link.component";

@Component({
  selector: "app-property-main",
  templateUrl: "./property-main.component.html",
  styleUrls: ["./property-main.component.scss"],
  standalone: true,
  imports: [
    NgIf,
    MatButtonModule,
    NgFor,
    RoundedLinkComponent,
    PropertySummaryComponent,
    TranslateModule,
    TooltipDirective,
  ],
})
export class PropertyMainComponent
  extends BasePageComponent
  implements OnInit, OnDestroy
{
  linkColor = "primary";

  prop: RealProperty;
  isDisabledPhotos = false;
  isDisabledServices = false;
  isDisabledAcces = false;
  hasSubscriptionAlert = false;
  condoIcon: string;

  canUpdateProperty = false;

  list: MenuToNative[];

  subs: Subscription[] = [];

  constructor(
    protected route: ActivatedRoute,
    private router: Router,
    private appConfigService: AppConfigService,
    public propHelper: PropertyHelper,
    private userHelper: UserHelper,
    protected navService: NavService,
    private dialog: MatDialog,
    private sharingService: SharingService,
    private alertService: AlertService,
    private translate: TranslateService,
    private propertiesService: PropertiesService,
    private actionService: ActionService,
    private cdref: ChangeDetectorRef
  ) {
    super(route, navService);
  }

  ngOnInit() {
    this.hasSubscriptionAlert = !this.userHelper.isCurrentSubscriptionActive();
    this.subs.push(
      this.appConfigService.selectedPropObs.subscribe((p) => {
        if (p) {
          this.prop = p;
          this.canUpdateProperty = this.userHelper.canUpdateProperty(this.prop);
          // need to reset the list and force detection changes for menu kpi to be updated in the view... why ?
          this.list = [];
          this.cdref.detectChanges();
          this.initMenuList();
          this.loadData();
        }
      })
    );
  }

  initMenuList() {
    this.condoIcon = this.propHelper.getBadgeImageName(this.prop.type);
    this.isDisabledServices = !this.canUpdateProperty;
    this.isDisabledAcces = !this.userHelper.canViewCategory(
      this.prop,
      "SHARING"
    );
    this.isDisabledPhotos =
      this.userHelper.canViewCategory(this.prop, "PHOTO") ||
      this.userHelper.isRescueService();
    this.appConfigService.currentDocCategory = DocumentCategory.BUILDING;

    const list: MenuToNative[] = [];
    list.push(this.navService.getNavEnvById(MenuToNativeId.PROP_INFO));
    list.push(this.navService.getNavEnvById(MenuToNativeId.PHOTO));
    list.push(this.navService.getNavEnvById(MenuToNativeId.TRACKING));
    list.push(this.navService.getNavEnvById(MenuToNativeId.VAULT));
    const share = this.navService.getNavEnvById(MenuToNativeId.PROP_SHARES);
    share.disabled = this.isDisabledAcces;
    list.push(share);
    const services = this.navService.getNavEnvById(
      MenuToNativeId.PROP_SERVICES
    );
    services.disabled = this.isDisabledServices;
    list.push(services);
    if (this.prop.onGoingSelling)
      list.push(this.navService.getNavEnvById(MenuToNativeId.PROP_SALE));

    // property attached to the condo
    if (this.propHelper.isInCondo(this.prop)) {
      const l = this.navService.getNavEnvById(MenuToNativeId.MAIN_CONDO);
      l.link = this.propHelper.getPropRouterLink(this.prop.condo).join("/");
      l.kpi = 1;
      list.push(l);
    }
    list.push(this.navService.getNavEnvById(MenuToNativeId.BOOKLET));
    //filter removed menus
    this.list = list.filter((m) => !m.removed);
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  confirmDeleteDemoProp() {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        // width: '25rem',
        data: {
          message: "Confirmez-vous la suppression de ce bien de démo ?",
        },
      });
      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doDeleteDemoProp();
        }
      });
    }
  }

  doDeleteDemoProp() {
    this.sharingService
      .deleteSharing(this.prop.details.accessSharing.uid)
      .subscribe(() => {
        this.alertService.success("Bien de démo supprimé");
        this.router.navigate(["/home"]);
      });
  }

  loadData() {
    if (this.prop.onGoingSelling) {
      this.list = this.navService.updateMenuSubTitle(
        this.list,
        MenuToNativeId.PROP_SALE,
        this.translate.instant(
          "enum.SellingStatus." + this.prop.onGoingSelling.status
        )
      );
    }
  }
}
